/* eslint-disable import/no-cycle */
import { RequestResult, SaveResult } from '@/composables/GraphQL';
import {
  OperatorType,
  ShopsSendDomains,
  ShopsSendDomainsInputItem,
  ShopsSendDomainsTokenInput,
  ShopsSendDomainsUpdateInputItem,
} from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import { showToastError } from '@/helpers';
import { NestApiType, nestCall, toQueryString } from '@/composables/nestApi';

interface DomainDnsStatusDto {
  verified: boolean;
  record: string;
}

interface DomainAuthenticationStatusDto {
  verified: number;
  selectors: string[];
  dkimSelector: string;
  dkimVerified: boolean;
  spfVerified: boolean;
}

interface DomainAlignmentStatusDto {
  verified: number;
  subdomain: string | null;
  expectedTarget: string;
}

interface DomainTrackingStatusDto {
  verified: boolean;
  subdomain: string | null;
  defaultForMedias: boolean;
  expectedTarget: string;
  url: string | null;
}

interface DomainResponseDto {
  id: number;
  shopId: number | null;
  domain: string;
  dnsVerificationKey: string | null;
  publicKey: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  dns: DomainDnsStatusDto;
  authentication: DomainAuthenticationStatusDto;
  alignment: DomainAlignmentStatusDto;
  tracking: DomainTrackingStatusDto;
}

interface DomainActionResponseDto {
  success: boolean;
  message?: string;
  domain?: DomainResponseDto;
}

interface DomainListItemDto {
  id_domain: number;
  domain: string;
  id_shop: number | null;
  domain_verified: boolean | number;
  dns_verification_key: string | null;
  public_key: string | null;
  dkim_verified: boolean | number;
  spf_verified: boolean | number;
  authentication_verified: number;
  alignment_domain: string | null;
  alignment_verified: boolean | number;
  dkim_selector: string | null;
  tracking_domain: string | null;
  tracking_domain_verified: boolean | number;
  tracking_domain_default_medias: boolean | number;
  date_modification?: string | Date | null;
  date_add?: string | Date | null;
}

interface DomainsListResponseDto {
  data: DomainListItemDto[];
  total: number;
  err: string;
}

interface NestCallError {
  error: any;
}

const isNestCallError = (response: unknown): response is NestCallError => (
  Boolean(response) && typeof response === 'object' && 'error' in (response as Record<string, unknown>)
);

const extractErrorMessage = (error: any): string => {
  if (!error) {
    return 'GENERIC_ERROR';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (Array.isArray(error?.message)) {
    return error.message.join(', ');
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  if (typeof error?.error === 'string') {
    return error.error;
  }

  return 'GENERIC_ERROR';
};

const getErrorStatus = (error: any): number | undefined => {
  if (!error) {
    return undefined;
  }

  if (typeof error.statusCode === 'number') {
    return error.statusCode;
  }

  if (typeof error.status === 'number') {
    return error.status;
  }

  return undefined;
};

const toBoolean = (value: number | boolean | null | undefined): boolean => Boolean(value);

const toMaybeDateString = (value: string | Date | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  return value.toISOString();
};

const mapDomainResponseToShopsSendDomain = (domain: DomainResponseDto): ShopsSendDomains => ({
  __typename: 'ShopsSendDomains',
  id_domain: domain.id,
  domain: domain.domain,
  id_shop: domain.shopId ?? null,
  domain_verified: domain.dns.verified,
  dns_verification_key: domain.dnsVerificationKey ?? null,
  public_key: domain.publicKey ?? null,
  dkim_verified: domain.authentication.dkimVerified,
  spf_verified: domain.authentication.spfVerified,
  authentication_verified: domain.authentication.verified,
  alignment_domain: domain.alignment.subdomain,
  alignment_verified: toBoolean(domain.alignment.verified),
  dkim_selector: domain.authentication.dkimSelector,
  tracking_domain: domain.tracking.subdomain,
  tracking_domain_verified: domain.tracking.verified,
  tracking_domain_default_medias: domain.tracking.defaultForMedias,
  date_modification: toMaybeDateString(domain.updatedAt),
  date_add: toMaybeDateString(domain.createdAt),
});

const mapDomainListItemToShopsSendDomain = (domain: DomainListItemDto): ShopsSendDomains => ({
  __typename: 'ShopsSendDomains',
  id_domain: domain.id_domain,
  domain: domain.domain,
  id_shop: domain.id_shop ?? null,
  domain_verified: toBoolean(domain.domain_verified),
  dns_verification_key: domain.dns_verification_key ?? null,
  public_key: domain.public_key ?? null,
  dkim_verified: toBoolean(domain.dkim_verified),
  spf_verified: toBoolean(domain.spf_verified),
  authentication_verified: domain.authentication_verified,
  alignment_domain: domain.alignment_domain ?? null,
  alignment_verified: toBoolean(domain.alignment_verified),
  dkim_selector: domain.dkim_selector ?? '',
  tracking_domain: domain.tracking_domain ?? null,
  tracking_domain_verified: toBoolean(domain.tracking_domain_verified),
  tracking_domain_default_medias: toBoolean(domain.tracking_domain_default_medias),
  date_modification: toMaybeDateString(domain.date_modification ?? null),
  date_add: toMaybeDateString(domain.date_add ?? null),
});

const normalizeFilterValue = (value: any): any => {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();

  if (trimmed.toLowerCase() === 'true') {
    return true;
  }

  if (trimmed.toLowerCase() === 'false') {
    return false;
  }

  const numeric = Number(trimmed);

  if (!Number.isNaN(numeric) && trimmed !== '') {
    return numeric;
  }

  return trimmed;
};

const matchesFilter = (item: ShopsSendDomains, filter: any): boolean => {
  const {
    field, operator, value, values,
  } = filter ?? {};

  if (!field || !operator) {
    return true;
  }

  const fieldValue = (item as Record<string, any>)[field];
  const normalizedValue = normalizeFilterValue(value);
  const normalizedValues = Array.isArray(values)
    ? values.map((val) => normalizeFilterValue(val))
    : undefined;

  switch (operator) {
    case OperatorType.Equals:
      return fieldValue === normalizedValue;
    case OperatorType.NotEquals:
      return fieldValue !== normalizedValue;
    case OperatorType.In: {
      const lookup = normalizedValues ?? (Array.isArray(normalizedValue) ? normalizedValue : [normalizedValue]);
      return lookup.some((val) => val === fieldValue);
    }
    case OperatorType.StartsWith:
      return typeof fieldValue === 'string'
        && typeof normalizedValue === 'string'
        && fieldValue.toLowerCase().startsWith(normalizedValue.toLowerCase());
    case OperatorType.EndsWith:
      return typeof fieldValue === 'string'
        && typeof normalizedValue === 'string'
        && fieldValue.toLowerCase().endsWith(normalizedValue.toLowerCase());
    case OperatorType.Contains:
    case OperatorType.Like:
      return typeof fieldValue === 'string'
        && typeof normalizedValue === 'string'
        && fieldValue.toLowerCase().includes(normalizedValue.toLowerCase());
    case OperatorType.NotContains:
      return typeof fieldValue === 'string'
        && typeof normalizedValue === 'string'
        && !fieldValue.toLowerCase().includes(normalizedValue.toLowerCase());
    case OperatorType.IsNull:
      return fieldValue === null || fieldValue === undefined || fieldValue === '';
    case OperatorType.IsNotNull:
      return !(fieldValue === null || fieldValue === undefined || fieldValue === '');
    case OperatorType.OrIsNull:
      return fieldValue === normalizedValue
        || fieldValue === null
        || fieldValue === undefined
        || fieldValue === '';
    case OperatorType.GreaterThan:
    case OperatorType.GreaterThanOrEqualTo:
    case OperatorType.LessThan:
    case OperatorType.LessThanOrEqualTo: {
      const numericField = typeof fieldValue === 'number' ? fieldValue : Number(fieldValue);
      const numericValue = typeof normalizedValue === 'number' ? normalizedValue : Number(normalizedValue);

      if (Number.isNaN(numericField) || Number.isNaN(numericValue)) {
        return false;
      }

      if (operator === OperatorType.GreaterThan) {
        return numericField > numericValue;
      }

      if (operator === OperatorType.GreaterThanOrEqualTo) {
        return numericField >= numericValue;
      }

      if (operator === OperatorType.LessThan) {
        return numericField < numericValue;
      }

      return numericField <= numericValue;
    }
    default:
      return true;
  }
};

const applyFilters = (domains: ShopsSendDomains[], filters: any[]): ShopsSendDomains[] => {
  if (!filters || filters.length === 0) {
    return domains;
  }

  return domains.filter((domain) => filters.every((filter) => matchesFilter(domain, filter)));
};

const fetchDomains = async (filters: any[] = []): Promise<{ domains: ShopsSendDomains[]; error?: string }> => {
  const query = toQueryString({
    settings: JSON.stringify({
      limit: 0,
      offset: 0,
      order: [],
      filter: filters,
    }),
  });

  const response = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    '/domains',
    {},
    `?${query}`,
  );

  if (!response) {
    return { domains: [], error: 'GENERIC_ERROR' };
  }

  if (isNestCallError(response)) {
    return { domains: [], error: extractErrorMessage(response.error) };
  }

  const listResponse = response as DomainsListResponseDto;
  if (listResponse.err) {
    return { domains: [], error: listResponse.err };
  }

  const items = Array.isArray(listResponse.data) ? listResponse.data : [];
  return {
    domains: items.map((domain) => mapDomainListItemToShopsSendDomain(domain)),
  };
};

export default async function getShopsDomains(limit = 10, filters: any = [], _additionalFields: any = []): Promise<ShopsSendDomains[]> {
  if (_additionalFields && _additionalFields.length > 0) {
    // Additional fields are handled by the API; the parameter is kept for compatibility.
  }
  const defaultFilters = [
    {
      field: 'id_shop',
      operator: OperatorType.Equals,
      value: UserState.activeShop?.id ?? 0,
    },
  ];

  const mergedFilters = filters.length > 0 ? [...defaultFilters, ...filters] : defaultFilters;
  const { domains, error } = await fetchDomains(mergedFilters);

  if (error) {
    await showToastError(error);
    return [];
  }

  const orderedDomains = domains.sort((a, b) => a.id_domain - b.id_domain);

  if (limit > 0) {
    return orderedDomains.slice(0, limit);
  }

  return orderedDomains;
}

export const CheckDomainValidForAlignment = async (_idShop: number, idDomain: number, domain: string): Promise<RequestResult<boolean>> => {
  if (_idShop) {
    // Shop identifier is unused; preserved for backward compatibility.
  }
  const response = await nestCall('post', NestApiType.CLIENT_API, `/domains/${idDomain}/align`, {}, '', {
    subdomain: domain,
  });

  if (!response) {
    return { data: null, err: 'GENERIC_ERROR' };
  }

  if (isNestCallError(response)) {
    const status = getErrorStatus(response.error);

    if (status && status >= 400 && status < 500) {
      return { data: false, err: '' };
    }

    return { data: null, err: extractErrorMessage(response.error) };
  }

  const actionResponse = response as DomainActionResponseDto;

  if (!actionResponse.success) {
    return { data: false, err: actionResponse.message ?? '' };
  }

  return { data: true, err: '' };
};

export const CheckDomainValidForTracking = async (_idShop: number, idDomain: number, domain: string): Promise<RequestResult<boolean>> => {
  if (_idShop) {
    // Shop identifier is unused; preserved for backward compatibility.
  }
  const response = await nestCall('post', NestApiType.CLIENT_API, `/domains/${idDomain}/redirect`, {}, '', {
    subdomain: domain,
    setAsDefault: false,
  });

  if (!response) {
    return { data: null, err: 'GENERIC_ERROR' };
  }

  if (isNestCallError(response)) {
    const status = getErrorStatus(response.error);

    if (status && status >= 400 && status < 500) {
      return { data: false, err: '' };
    }

    return { data: null, err: extractErrorMessage(response.error) };
  }

  const actionResponse = response as DomainActionResponseDto;

  if (!actionResponse.success) {
    return { data: false, err: actionResponse.message ?? '' };
  }

  return { data: true, err: '' };
};

export const MakeDomainDefault = async (
  shopsSendDomainsInput: ShopsSendDomainsUpdateInputItem[],
): Promise<SaveResult<ShopsSendDomainsUpdateInputItem>> => {
  if (!shopsSendDomainsInput || shopsSendDomainsInput.length === 0) {
    return {
      id: 0,
      status: false,
      messages: [],
      err: 'GENERIC_ERROR',
    };
  }

  let lastResponse: DomainActionResponseDto | null = null;

  // Ensure sequential execution to keep behaviour consistent with legacy GraphQL mutations
  // eslint-disable-next-line no-restricted-syntax
  for (const input of shopsSendDomainsInput) {
    // eslint-disable-next-line no-await-in-loop
    const response = await nestCall('put', NestApiType.CLIENT_API, `/domains/${input.id_domain}/default`, {}, '', {
      defaultForMedias: input.tracking_domain_default_medias ?? true,
    });

    if (!response) {
      return {
        id: 0,
        status: false,
        messages: [],
        err: 'GENERIC_ERROR',
      };
    }

    if (isNestCallError(response)) {
      return {
        id: 0,
        status: false,
        messages: [{ message: extractErrorMessage(response.error) }],
        err: extractErrorMessage(response.error),
      };
    }

    const actionResponse = response as DomainActionResponseDto;

    if (!actionResponse.success) {
      const message = actionResponse.message ?? 'GENERIC_ERROR';
      return {
        id: actionResponse.domain?.id ?? 0,
        status: false,
        messages: message ? [{ message }] : [],
        err: message,
      };
    }

    lastResponse = actionResponse;
  }

  return {
    id: lastResponse?.domain?.id ?? 0,
    status: true,
    messages: lastResponse?.message ? [{ message: lastResponse.message }] : [],
    err: '',
  };
};

export const getShopsDomainsCount = async (idShop: number): Promise<number> => {
  const { domains, error } = await fetchDomains();

  if (error) {
    await showToastError(error);
    return 0;
  }

  if (!idShop) {
    return domains.length;
  }

  return domains.filter((domain) => domain.id_shop === idShop).length;
};

export const InsertShopsSendDomains = async (
  shopsSendDomains: ShopsSendDomainsInputItem[],
): Promise<SaveResult<ShopsSendDomainsInputItem>> => {
  if (!shopsSendDomains || shopsSendDomains.length === 0) {
    return {
      id: 0,
      status: false,
      messages: [],
      err: 'GENERIC_ERROR',
    };
  }

  let lastResponse: DomainActionResponseDto | null = null;

  // eslint-disable-next-line no-restricted-syntax
  for (const domainInput of shopsSendDomains) {
    const payload = {
      domain: domainInput.domain,
    };

    // eslint-disable-next-line no-await-in-loop
    const response = await nestCall('post', NestApiType.CLIENT_API, '/domains', {}, '', payload);

    if (!response) {
      return {
        id: 0,
        status: false,
        messages: [],
        err: 'GENERIC_ERROR',
      };
    }

    if (isNestCallError(response)) {
      const message = extractErrorMessage(response.error);
      return {
        id: 0,
        status: false,
        messages: message ? [{ message }] : [],
        err: message,
      };
    }

    const actionResponse = response as DomainActionResponseDto;

    if (!actionResponse.success) {
      const message = actionResponse.message ?? 'GENERIC_ERROR';
      return {
        id: actionResponse.domain?.id ?? 0,
        status: false,
        messages: message ? [{ message }] : [],
        err: message,
      };
    }

    lastResponse = actionResponse;
  }

  return {
    id: lastResponse?.domain?.id ?? 0,
    status: true,
    messages: lastResponse?.message ? [{ message: lastResponse.message }] : [],
    err: '',
  };
};

export const DeleteShopsSendDomains = async (
  shopsSendDomains: ShopsSendDomainsUpdateInputItem[],
): Promise<SaveResult<ShopsSendDomainsUpdateInputItem>> => {
  if (!shopsSendDomains || shopsSendDomains.length === 0) {
    return {
      id: 0,
      status: false,
      messages: [],
      err: 'GENERIC_ERROR',
    };
  }

  let lastMessage: string | undefined;

  // eslint-disable-next-line no-restricted-syntax
  for (const domainInput of shopsSendDomains) {
    // eslint-disable-next-line no-await-in-loop
    const response = await nestCall('delete', NestApiType.CLIENT_API, `/domains/${domainInput.id_domain}`);

    if (!response) {
      return {
        id: 0,
        status: false,
        messages: [],
        err: 'GENERIC_ERROR',
      };
    }

    if (isNestCallError(response)) {
      const message = extractErrorMessage(response.error);
      return {
        id: 0,
        status: false,
        messages: message ? [{ message }] : [],
        err: message,
      };
    }

    const actionResponse = response as DomainActionResponseDto;

    if (!actionResponse.success) {
      const message = actionResponse.message ?? 'GENERIC_ERROR';
      return {
        id: actionResponse.domain?.id ?? 0,
        status: false,
        messages: message ? [{ message }] : [],
        err: message,
      };
    }

    lastMessage = actionResponse.message;
  }

  return {
    id: shopsSendDomains[0].id_domain ?? 0,
    status: true,
    messages: lastMessage ? [{ message: lastMessage }] : [],
    err: '',
  };
};

export const VerifyDomain = async (idDomain: number): Promise<RequestResult<ShopsSendDomains>> => {
  const response = await nestCall('post', NestApiType.CLIENT_API, `/domains/${idDomain}/verify/dns`);

  if (!response) {
    return { data: null, err: 'GENERIC_ERROR' };
  }

  if (isNestCallError(response)) {
    return { data: null, err: extractErrorMessage(response.error) };
  }

  const actionResponse = response as DomainActionResponseDto;

  if (!actionResponse.domain) {
    return {
      data: null,
      err: actionResponse.message ?? '',
    };
  }

  return {
    data: mapDomainResponseToShopsSendDomain(actionResponse.domain),
    err: '',
  };
};

export const ValidateDomainByEmail = async (_idShop: number, idDomain: number, mailWithoutDomain: string) => {
  if (_idShop) {
    // Shop identifier is unused; preserved for backward compatibility.
  }

  return nestCall('post', NestApiType.CLIENT_API, `/domains/${idDomain}/verify/email`, {}, '', {
    localPart: mailWithoutDomain,
  });
};

export const ValidateShopDomainByToken = async (
  token: string,
  _idShop: number,
): Promise<SaveResult<ShopsSendDomainsTokenInput>> => {
  if (_idShop) {
    // Shop identifier is unused; preserved for backward compatibility.
  }
  const response = await nestCall('post', NestApiType.CLIENT_API, '/domains/verify/email-token', {}, '', {
    token,
  });

  if (!response) {
    return {
      id: 0,
      status: false,
      messages: [],
      err: 'GENERIC_ERROR',
    };
  }

  if (isNestCallError(response)) {
    const message = extractErrorMessage(response.error);
    return {
      id: 0,
      status: false,
      messages: message ? [{ message }] : [],
      err: message,
    };
  }

  const actionResponse = response as DomainActionResponseDto;

  if (!actionResponse.success || !actionResponse.domain) {
    const message = actionResponse.message ?? 'GENERIC_ERROR';
    return {
      id: actionResponse.domain?.id ?? 0,
      status: false,
      messages: message ? [{ message }] : [],
      err: message,
    };
  }

  return {
    id: actionResponse.domain.id,
    status: true,
    messages: actionResponse.message ? [{ message: actionResponse.message }] : [],
    err: '',
  };
};
